import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { Switch, Route, useRouteMatch, useLocation, Redirect } from 'react-router-dom';

import { TreeNav } from 'ui-components/components/tree-navigation';

import analyticsStyle from './analytics-style.module.less';
import Breadcrumbs from './components/breadcrumbs';
import InsightsPageWrapper from './components/insights-page-wrapper';
import { DASHBOARD, INSIGHT_PAGES, REPORT, ANALYTICS_VIEW_TYPE } from './constants';
import { LOOKER_PAGES } from './looker';
import { insightNavItems, analyticsNavItems, InsightsPageNavItemIds } from './nav-items';
import { Reports } from './reports';
import { AnalyticsReportsPage } from './reports/analytics-reports-page';
import { REPORTS_LINKS, REPORTS_PAGES } from './reports/constants';
import Report from './reports/report';
import style from './style.module.less';
import ToggleLeftNav from '../../components/toggle-left-nav';
import { SHIPSTATION_ANALYTICS_DASHBOARD_CLICK } from '../../constants/analytics-events';
import { messages } from '../../feature-gates/messages';
import { useDashboardsViewOnlyFeatureGate } from '../../feature-gates/use-dashboards-view-only-feature-gate';
import {
  useIsShipStationAnalyticsFeatureEnabled,
  useGetShipStationAnalyticsUserType,
} from '../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { useReportsViewOnlyFeatureGate } from '../../feature-gates/use-reports-view-only-feature-gate';
import { useEnableConversationalAnalytics } from '../../feature-flags/hooks/use-enable-conversational-analytics';
import { Features } from '../../constants/features';
import { useIsSellerFeatureEnabled } from '../../hooks/use-is-seller-feature-enabled';
import { useSellerIsInTrial } from '../../hooks/use-seller-is-in-trial';
import { useBTEmailExperienceNew } from '../../hooks/use-seller-setting';
import { Segment } from '../../services/analytics-services/segment';
import { UserPermissionService } from '../../services/user-permission-service';
import Global from '../../store/global-selectors';
import { ANALYTICS_NAV_ITEM_ID, EXPLORE_NAV_ITEM_ID } from '../insights/looker/constants';

const REPORTS_BASE_PATH = `/${DASHBOARD}/${REPORT}`;

const InsightsPageRedirect = ({ isDashboardsEnabled, isReportsEnabled, defaultDashboard }) => {
  const userRoles = useSelector(Global.selectUseruserRoles);

  if (isDashboardsEnabled) {
    return <Redirect to={defaultDashboard} />;
  }

  if (isReportsEnabled) {
    return <Redirect to={REPORTS_BASE_PATH} />;
  }

  return <Redirect to={UserPermissionService.getFirstPermissableRouteUrl(userRoles)} />;
};

export const InsightsPage = () => {
  const isShipStationAnalyticsEnabled = useIsShipStationAnalyticsFeatureEnabled();
  const shipStationAnalyticsSellerType = useGetShipStationAnalyticsUserType() || ANALYTICS_VIEW_TYPE.locked;
  const isInTrial = useSellerIsInTrial();

  const hasPremiumAnalytics = useIsSellerFeatureEnabled(Features.PremiumAnalytics);

  const [navItems, setNavItems] = useState(isShipStationAnalyticsEnabled ? analyticsNavItems : insightNavItems);
  const urlLocation = useLocation();
  const enableNewEmailExperience = useBTEmailExperienceNew();
  const isConversationalAnalyticsEnabled = useEnableConversationalAnalytics();
  const { isFeatureEnabled: isDashboardsEnabled } = useDashboardsViewOnlyFeatureGate();
  const { isFeatureEnabled: isReportsEnabled } = useReportsViewOnlyFeatureGate();

  useEffect(() => {
    if (isShipStationAnalyticsEnabled) {
      const payload = {
        location: urlLocation.pathname,
        shipStationAnalyticsSellerType,
      };
      Segment.trackEvent(SHIPSTATION_ANALYTICS_DASHBOARD_CLICK, payload);
    }
  }, [isShipStationAnalyticsEnabled, shipStationAnalyticsSellerType, urlLocation.pathname]);

  const DEFAULT_DASHBOARD_PAGE_PATH = isShipStationAnalyticsEnabled
    ? `/${DASHBOARD}/${ANALYTICS_NAV_ITEM_ID.shipmentOverview}`
    : `/${DASHBOARD}/${INSIGHT_PAGES.overview}`;

  const match = useRouteMatch({
    path: `/${DASHBOARD}/:page`,
    strict: true,
    sensitive: true,
  });
  const page = match?.params?.page;

  const buildTagProps = useCallback(
    (view) => {
      if (view.location === REPORT) {
        return { to: `/${DASHBOARD}/${view.location}` };
      }
      if (Array.isArray(view.items) && view.items.length > 0) {
        const firstChild = view.items[0];
        const isFirstChildActive = page === firstChild.location;
        return { to: isFirstChildActive ? `${urlLocation.pathname}${urlLocation.search}` : `/${DASHBOARD}/${firstChild.location}` };
      }
      if (view.location?.startsWith('explore-')) {
        const isActive = page === view.location;
        return { to: isActive ? `${urlLocation.pathname}${urlLocation.search}` : `/${DASHBOARD}/${view.location}` };
      }
      return { to: `/${DASHBOARD}/${view.location}${urlLocation.search}` };
    },
    [urlLocation, page],
  );

  const onExpandToggle = useCallback(
    ({ location }) => {
      setNavItems(
        navItems.map((nav) => ({
          ...nav,
          ...(typeof nav.expanded !== 'undefined' && {
            expanded: nav.location === location ? !nav.expanded : nav.expanded,
          }),
        })),
      );
    },
    [navItems],
  );

  const onSelected = useCallback(
    ({ location }) => {
      if (isShipStationAnalyticsEnabled) {
        setNavItems(
          navItems.map((nav) => {
            return {
              ...nav,
              selected: nav.location === location,
              ...(typeof nav.expanded !== 'undefined' && {
                expanded:
                  nav.location === location ? true : !!(Array.isArray(nav.items) && nav.items.some((item) => item.location === location)),
              }),
              ...(Array.isArray(nav.items) && {
                items: nav.items.map((item) => ({
                  ...item,
                  selected: item.location === location,
                })),
              }),
            };
          }),
        );

        return;
      }

      setNavItems(
        navItems.map((item) => ({
          ...item,
          selected: item.location === location,
        })),
      );
    },
    [navItems, isShipStationAnalyticsEnabled],
  );

  useEffect(() => {
    const navItem =
      navItems.find((item) => item.location === page) ||
      navItems.flatMap((item) => item.items || []).find((child) => child.location === page);
    if (navItem && !navItem.selected) {
      onSelected({ location: page });
    }
  }, [navItems, onSelected, page]);

  const filteredNavItems = useMemo(() => {
    if (isShipStationAnalyticsEnabled) {
      // Filter out Conversational Analytics when Labs feature is not enabled
      const visibleNavItems = navItems.filter((item) => !item.isConversationalAnalytics || isConversationalAnalyticsEnabled);

      // Advanced users see all the nav items (except Data Explorer if not premium)
      if (shipStationAnalyticsSellerType === ANALYTICS_VIEW_TYPE.full) {
        if (!hasPremiumAnalytics) {
          return visibleNavItems.filter((item) => item.id !== EXPLORE_NAV_ITEM_ID.dataExplorer);
        }
        return visibleNavItems;
      }

      // Basic users see all nav items except Looker items
      if (shipStationAnalyticsSellerType === ANALYTICS_VIEW_TYPE.partial) {
        return visibleNavItems
          .filter((item) => {
            // Filter out top-level Looker items
            if (item.isLooker) {
              return false;
            }

            return true;
          })
          .map((item) => {
            // Return the items with no nested items
            return {
              ...item,
              expanded: false,
              items: null,
            };
          });
      }

      // Starter users see all nav items, but with locks on Looker items
      return visibleNavItems.map((item) => {
        const isLocked = shipStationAnalyticsSellerType === ANALYTICS_VIEW_TYPE.locked && item.isLooker;

        // Process nested items if they exist
        const processedItem = {
          ...item,
          isLocked,
          // Add the custom tooltip message if the item is locked
          lockTooltipMessage: isInTrial ? messages.trialWithNoLabels : messages.featureNotAvailableOnCurrentPlan,
        };

        // If this item has nested items, map those too
        if (Array.isArray(item.items)) {
          processedItem.items = item.items.map((subItem) => {
            const isSubItemLocked = shipStationAnalyticsSellerType === ANALYTICS_VIEW_TYPE.locked && subItem.isLooker;
            return {
              ...subItem,
              isLocked: isSubItemLocked,
              // Add the custom tooltip message if the subitem is locked
              lockTooltipMessage: isInTrial ? messages.trialWithNoLabels : messages.featureNotAvailableOnCurrentPlan,
            };
          });
        }

        return processedItem;
      });
    }
    // Original filtering logic when analytics is not enabled
    return navItems.filter((item) => {
      // Hide the Customer Engagement nav item if the new email experience is enabled
      if (enableNewEmailExperience && item.id === InsightsPageNavItemIds.CustomerEngagement) {
        return false;
      }

      if (item.id === InsightsPageNavItemIds.Reports) {
        return isReportsEnabled;
      }

      return isDashboardsEnabled;
    });
  }, [
    navItems,
    enableNewEmailExperience,
    isDashboardsEnabled,
    isReportsEnabled,
    isShipStationAnalyticsEnabled,
    shipStationAnalyticsSellerType,
    isInTrial,
    isConversationalAnalyticsEnabled,
    hasPremiumAnalytics,
  ]);

  return (
    <div className={style['page-wrapper']}>
      <ToggleLeftNav className={style['toggle-icon']}>
        <div
          className={classnames(style.leftnav, {
            [analyticsStyle['analytics-left-nav-with-locks']]: shipStationAnalyticsSellerType === ANALYTICS_VIEW_TYPE.locked,
          })}
        >
          <TreeNav buildTagProps={buildTagProps} items={filteredNavItems} onExpandClick={onExpandToggle} onItemSelected={onSelected} />
        </div>
      </ToggleLeftNav>
      <div className={style.page}>
        <Switch>
          {isReportsEnabled || isShipStationAnalyticsEnabled
            ? REPORTS_PAGES.map((reportPageId) => (
                <Route key={reportPageId} path={`${REPORTS_BASE_PATH}/${REPORTS_LINKS[reportPageId]}`}>
                  <Breadcrumbs />
                  <Report page={reportPageId} />
                </Route>
              ))
            : null}
          {isShipStationAnalyticsEnabled
            ? LOOKER_PAGES.filter((p) => p.isLooker && p.Component)
                .filter((p) => !p.isConversationalAnalytics || isConversationalAnalyticsEnabled)
                .map((p) => {
                  return <Route key={p.id} component={p.Component} path={p.path} />;
                })
            : null}
          {isReportsEnabled || isShipStationAnalyticsEnabled ? (
            <Route path={REPORTS_BASE_PATH}>{isShipStationAnalyticsEnabled ? <AnalyticsReportsPage /> : <Reports />}</Route>
          ) : null}
          {isDashboardsEnabled || isShipStationAnalyticsEnabled ? (
            <Route path={`/${DASHBOARD}/:page(${Object.keys(INSIGHT_PAGES).join('|')})`}>
              <InsightsPageWrapper />
            </Route>
          ) : null}
          <Route path='*'>
            <InsightsPageRedirect
              defaultDashboard={DEFAULT_DASHBOARD_PAGE_PATH}
              isDashboardsEnabled={isDashboardsEnabled}
              isReportsEnabled={isReportsEnabled}
            />
          </Route>
        </Switch>
      </div>
    </div>
  );
};
